import type { RefObject } from 'react'

import { useEffect, useLayoutEffect, useRef } from 'react'

import { gsap, registerMotion, ScrollTrigger, prefersReducedMotion } from '@/lib/motion'

/**
 * `useLayoutEffect` runs before the browser paints, which is what stops a flash of
 * un-animated content. It is not available during SSR, so fall back there.
 */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

type Setup = (context: {
    gsap: typeof gsap
    scope: HTMLElement
    select: (selector: string) => HTMLElement[]
    reduced: boolean
}) => void

/**
 * Runs a GSAP setup inside a scoped context, on the client only.
 * Everything created inside is reverted automatically on unmount, which keeps
 * ScrollTriggers from leaking across page transitions.
 */
export const useGsap = <T extends HTMLElement = HTMLElement>(
    setup: Setup,
    deps: unknown[] = []
): RefObject<T | null> => {
    const scope = useRef<T | null>(null)
    const setupRef = useRef(setup)

    setupRef.current = setup

    useIsomorphicLayoutEffect(() => {
        const element = scope.current

        if (!element) return

        registerMotion()

        const reduced = prefersReducedMotion()

        const ctx = gsap.context((self) => {
            setupRef.current({
                gsap,
                scope: element,
                select: (selector: string) => (self.selector?.(selector) ?? []) as HTMLElement[],
                reduced
            })
        }, element)

        // Recalculate once fonts and images have settled so triggers land in the right place.
        const refresh = (): void => ScrollTrigger.refresh()
        const timer = window.setTimeout(refresh, 400)

        window.addEventListener('load', refresh)

        return () => {
            window.clearTimeout(timer)
            window.removeEventListener('load', refresh)
            ctx.revert()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)

    return scope
}
